import React from 'react';
import { Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/utils';
import type { FilterItem } from './types';
import { coerceFilterBoolean } from './filterBoolean';
import { isTrendFilterLockedByMode } from '../filterModeRules';
import FilterFieldShell from './FilterFieldShell';
import { translateFilter } from './filterI18n';

// Import control components
import {
  NumberInput,
  SelectInput,
  SegmentedControl,
  ToggleSwitch,
  CardCheckboxGroup,
  CardRadioGroup,
  ToggleWithDetail,
} from './controls';

interface FilterContentProps {
  filters: FilterItem[];
  values: Record<string, any>;
  onChange: (id: string, value: any) => void;
  expandedDetails: Record<string, boolean>;
  onToggleDetail: (filterId: string) => void;
  onSetDetailExpanded: (filterId: string, open: boolean) => void;
  searchMode: boolean;
  className?: string;
}

// Helper to format number values for display (percent unit: store decimal, display as %)
const formatValue = (filter: FilterItem, value: any): any => {
  if (filter.type === 'number' && typeof value === 'number') {
    if (filter.unit === 'percent') {
      return value;
    }
    return value;
  }
  return value;
};

// For percent unit: value is stored as decimal (0.1 = 10%). Return display value and optional transform for onChange.
const getPercentDisplay = (
  filter: FilterItem,
  value: any
): { display: number; min: number; max: number; step: number } | null => {
  if (
    filter.type !== 'number' ||
    filter.unit !== 'percent' ||
    typeof value !== 'number'
  )
    return null;
  const min = (filter.min ?? 0) * 100;
  const max = (filter.max ?? 1) * 100;
  const step = (filter.step ?? 0.01) * 100 || 1;
  return { display: value * 100, min, max, step };
};

// Render a single filter control based on type.
// `omitLabel`: when the surrounding FilterFieldShell already renders the
// label-and-info row, suppress the control's internal label to avoid a
// duplicate two-line stack.
const renderFilterControl = (
  filter: FilterItem,
  value: any,
  onChange: (id: string, value: any) => void,
  disabled: boolean,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: any,
  controlClassName?: string,
  omitLabel: boolean = false
) => {
  const handleChange = (newValue: any) => {
    onChange(filter.id, newValue);
  };

  switch (filter.type) {
    case 'number': {
      const percentDisplay = getPercentDisplay(filter, value);
      const displayValue = percentDisplay ? percentDisplay.display : value;
      const min = percentDisplay ? percentDisplay.min : (filter.min ?? 0);
      const max = percentDisplay ? percentDisplay.max : (filter.max ?? 100);
      const step = percentDisplay ? percentDisplay.step : (filter.step ?? 1);
      const handleNumberChange = percentDisplay
        ? (v: number) => onChange(filter.id, v / 100)
        : handleChange;
      return (
        <NumberInput
          value={displayValue}
          onChange={handleNumberChange}
          min={min}
          max={max}
          step={step}
          prefix={filter.prefix ?? ''}
          suffix={filter.suffix ?? ''}
          {...(omitLabel ? {} : { label: filter.label })}
          disabled={disabled}
          {...(controlClassName ? { className: controlClassName } : {})}
        />
      );
    }

    case 'select':
      return (
        <SelectInput
          value={value}
          onChange={handleChange}
          options={filter.options || []}
          disabled={disabled}
          {...(omitLabel ? {} : { label: filter.label })}
        />
      );

    case 'segmented':
      return (
        <SegmentedControl
          value={value}
          onChange={handleChange}
          options={filter.options || []}
          disabled={disabled}
          {...(omitLabel ? {} : { label: filter.label })}
        />
      );

    case 'toggle':
      // Description intentionally omitted - FilterFieldShell renders it via
      // tooltip so each toggle row stays compact and avoids double display.
      return (
        <ToggleSwitch
          checked={value}
          onChange={handleChange}
          {...(filter.label !== undefined && { label: filter.label })}
          disabled={disabled}
          testId={filter.id}
        />
      );

    case 'card-checkbox-group':
      return (
        <CardCheckboxGroup
          value={value}
          onChange={handleChange}
          options={filter.options || []}
          hasSelectAll={filter.hasSelectAll ?? false}
          disabled={disabled}
        />
      );

    case 'card-radio-group':
      return (
        <CardRadioGroup
          value={value}
          onChange={handleChange}
          options={filter.options || []}
          disabled={disabled}
        />
      );

    case 'toggle-with-detail':
      // ToggleWithDetail is handled separately
      return null;

    default:
      return (
        <div className="text-sm text-text-muted">
          {t('errors.unknownType', { type: filter.type })}
        </div>
      );
  }
};

export const FilterContent: React.FC<FilterContentProps> = ({
  filters,
  values,
  onChange,
  expandedDetails,
  onToggleDetail,
  onSetDetailExpanded,
  searchMode,
  className,
}) => {
  const { t } = useTranslation();
  // Localise filter text (label / short / description / option labels) before
  // partitioning. Falls back to the English literal stored on each filter when
  // the translation key is missing - see filterI18n.ts.
  const localisedFilters = filters.map((f) => translateFilter(f, t));
  // Group filters by type for better layout
  const toggleFilters = localisedFilters.filter(
    (f) => f.type === 'toggle' || f.type === 'toggle-with-detail'
  );
  const valueFilters = localisedFilters.filter(
    (f) => f.type === 'number' || f.type === 'select' || f.type === 'segmented'
  );
  const groupFilters = localisedFilters.filter(
    (f) => f.type === 'card-checkbox-group' || f.type === 'card-radio-group'
  );

  // Handle toggle-with-detail specially
  const isTrendFilterLocked = (filter: FilterItem): boolean =>
    filter.id === 'bullish_momentum_enabled' &&
    isTrendFilterLockedByMode({
      standard_screening_enabled: coerceFilterBoolean(
        values.standard_screening_enabled,
        true
      ),
      active_category: String(values.active_category || ''),
    });

  const handleToggleWithDetail = (filter: FilterItem) => {
    const enabledKey = filter.enabledKey || filter.id;
    const enabled = coerceFilterBoolean(
      values[enabledKey],
      Boolean(filter.defaultValue)
    );
    const disabled = isTrendFilterLocked(filter);

    return (
      <ToggleWithDetail
        key={filter.id}
        filter={filter}
        value={enabled}
        onToggle={(val) => onChange(enabledKey, val)}
        configValues={values}
        onConfigChange={onChange}
        isExpanded={expandedDetails[filter.id] || false}
        onToggleExpand={() => onToggleDetail(filter.id)}
        onSetExpanded={(open: boolean) => onSetDetailExpanded(filter.id, open)}
        disabled={disabled}
      />
    );
  };

  if (searchMode && filters.length === 0) {
    return (
      <div
        className={cn(
          'flex flex-col items-center justify-center p-8 text-center',
          className
        )}
      >
        <Search className="w-12 h-12 text-text-muted mb-4" />
        <h3 className="text-lg font-semibold text-text-primary mb-2">
          {t('filters.noFiltersFound')}
        </h3>
        <p className="text-sm text-text-muted max-w-xs">
          {t('filters.noFiltersFoundDesc')}
        </p>
      </div>
    );
  }

  // Helper: derive the descriptive copy a filter exposes (short → fall back to long).
  const descFor = (filter: FilterItem) =>
    filter.shortDescription || filter.description;
  const secondaryFor = (filter: FilterItem) =>
    filter.shortDescription &&
    filter.description &&
    filter.shortDescription !== filter.description
      ? filter.description
      : undefined;

  return (
    <div
      className={cn(
        'flex flex-col gap-3 px-4 py-3 md:px-5 md:py-3 overflow-y-auto min-h-0 h-full',
        className
      )}
    >
      {/* Group filters (checkboxes, radio groups) - full width, single column. */}
      {groupFilters.length > 0 && (
        <div className="flex flex-col gap-3">
          {groupFilters.map((filter) => {
            const value = values[filter.id] ?? filter.defaultValue;
            return (
              <FilterFieldShell
                key={filter.id}
                label={filter.label}
                description={descFor(filter)}
                secondary={secondaryFor(filter)}
                hideInfo={!descFor(filter)}
              >
                {renderFilterControl(filter, value, onChange, false, t)}
              </FilterFieldShell>
            );
          })}
        </div>
      )}

      {/* Value filters (numbers, selects, segmented) - uniform 3-col grid.
          Rules: prox-min/prox-max disabled when ma_filter_selection includes 'NONE';
          require_recent_ma150_cross continues to render in the toggles section. */}
      {valueFilters.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 md:gap-3">
          {valueFilters.map((filter) => {
            const value = values[filter.id] ?? filter.defaultValue;
            const isMaProximityWithNone =
              (filter.id === 'ma_proximity_min' ||
                filter.id === 'ma_proximity_max') &&
              (values.ma_filter_selection || []).includes('NONE');
            const disabled = isMaProximityWithNone;
            const baseDesc = descFor(filter);
            const description =
              isMaProximityWithNone && baseDesc
                ? `${baseDesc} (${t('filters.disabledInPermissiveMode')})`
                : baseDesc;
            return (
              <FilterFieldShell
                key={filter.id}
                label={filter.label}
                description={description}
                secondary={secondaryFor(filter)}
                hideInfo={!description}
              >
                {renderFilterControl(
                  filter,
                  formatValue(filter, value),
                  onChange,
                  disabled,
                  t,
                  undefined,
                  true /* omitLabel - shell renders it */
                )}
              </FilterFieldShell>
            );
          })}
        </div>
      )}

      {/* Toggle filters - single column for readability. */}
      {toggleFilters.length > 0 && (
        <div className="flex flex-col gap-3">
          {toggleFilters.map((filter) => {
            if (filter.type === 'toggle-with-detail') {
              const baseDesc = descFor(filter);
              const description =
                isTrendFilterLocked(filter) && baseDesc
                  ? `${baseDesc} (${t('filters.enabledByTradingStyle')})`
                  : baseDesc;
              return (
                <FilterFieldShell
                  key={filter.id}
                  description={description}
                  secondary={secondaryFor(filter)}
                  hideInfo={!description}
                >
                  {handleToggleWithDetail(filter)}
                </FilterFieldShell>
              );
            }

            // Regular toggle
            const value = coerceFilterBoolean(
              values[filter.id],
              Boolean(filter.defaultValue)
            );
            const description = descFor(filter);
            return (
              <FilterFieldShell
                key={filter.id}
                description={description}
                secondary={secondaryFor(filter)}
                hideInfo={!description}
              >
                {renderFilterControl(filter, value, onChange, false, t)}
              </FilterFieldShell>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FilterContent;
